import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, Building2, Store, ArrowRight, CheckCircle, Zap } from 'lucide-react';
import { servicios, proyectos, testimonios, certificaciones } from '../mock';

const Inicio = () => {
  const statsRef = useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const iconMap = {
    Home: Home,
    Building2: Building2,
    Store: Store
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section relative py-24 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6 animate-slide-up">
              <div className="inline-block">
                <span className="px-4 py-2 bg-yellow-400/20 text-yellow-400 rounded-full text-sm font-semibold backdrop-blur-sm border border-yellow-400/30">
                  {/* Más de 15 años de experienci */}
                </span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold heading-font leading-tight">
                Soluciones
                <span className="block gradient-text">Eléctricas</span>
                Profesionales
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                Servicios eléctricos de alta calidad para particulares, instituciones y comercios.
                Cumplimos con los más altos estándares de seguridad y eficiencia.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/contacto">
                  <button className="btn-primary flex items-center space-x-2">
                    <span>Solicitar Cotización</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/proyectos">
                  <button className="btn-secondary">
                    Ver Proyectos
                  </button>
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1660330589693-99889d60181e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzgxNTgwNDU5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Electricista profesional"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-br from-blue-500/20 to-yellow-500/20 rounded-2xl blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Servicios Principales */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold heading-font mb-4">Nuestros Servicios</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ofrecemos soluciones integrales para cada tipo de cliente
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {servicios.map((servicio, index) => {
              const Icon = iconMap[servicio.icono];
              return (
                <div
                  key={servicio.id}
                  className="service-card bg-white rounded-xl p-8 shadow-lg"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold heading-font mb-3">{servicio.titulo}</h3>
                  <p className="text-gray-600 mb-6">{servicio.descripcion}</p>
                  <ul className="space-y-2 mb-6">
                    {servicio.detalles.slice(0, 3).map((detalle, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{detalle}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={servicio.id === 1 ? '/area-electrica' : servicio.id === 2 ? '/area-electrica' : '/area-electrica'}
                    className="text-blue-600 font-semibold hover:text-yellow-500 transition-colors inline-flex items-center space-x-2"
                  >
                    <span>Conocer más</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Estadísticas */}
      <section ref={statsRef} className="py-20 bg-gradient-to-br from-blue-900 to-blue-950 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {certificaciones.map((cert, index) => (
              <div
                key={cert.id}
                className={`text-center ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-number mb-2">{cert.nombre}</div>
                <p className="text-gray-300 text-sm">{cert.descripcion}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proyectos Destacados */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold heading-font mb-4">Proyectos Destacados</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Algunos de nuestros trabajos más recientes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {proyectos.slice(0, 3).map((proyecto) => (
              <div key={proyecto.id} className="project-card rounded-xl overflow-hidden shadow-lg group">
                <img
                  src={proyecto.imagen}
                  alt={proyecto.nombre}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="project-card-content absolute inset-0 flex flex-col justify-end p-6">
                  <span className="text-yellow-400 text-sm font-semibold mb-2">{proyecto.categoria}</span>
                  <h3 className="text-xl font-bold mb-2">{proyecto.nombre}</h3>
                  <p className="text-sm text-gray-200">{proyecto.descripcion}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/proyectos">
              <button className="btn-primary">
                Ver Todos los Proyectos
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold heading-font mb-4">Lo Que Dicen Nuestros Clientes</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonios.map((testimonio) => (
              <div key={testimonio.id} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex mb-4">
                  {[...Array(testimonio.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonio.testimonio}"</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonio.nombre}</p>
                  <p className="text-sm text-gray-600">{testimonio.cargo}</p>
                  <p className="text-sm text-blue-600">{testimonio.empresa}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-950 text-white">
        <div className="container mx-auto px-4 text-center">
          <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
          <h2 className="text-4xl lg:text-5xl font-bold heading-font mb-6">
            ¿Listo para tu Proyecto?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Contáctanos hoy y recibe una cotización personalizada sin compromiso
          </p>
          <Link to="/contacto">
            <button className="btn-primary text-lg px-8 py-4">
              Solicitar Cotización Gratis
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Inicio;